import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

const BG_IMG = "https://images.unsplash.com/photo-1587522384446-64daf3e2689a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwY2xlYW4lMjBkZXNrfGVufDB8fHx8MTc4NTI0NDQ5MXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");

  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginEmail, loginPass);
      toast.success("Bem-vindo!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (email, password) => {
    setLoginEmail(email);
    setLoginPass(password);
    setTab("login");
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* Left panel */}
      <div className="relative hidden overflow-hidden lg:block">
        <img src={BG_IMG} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10 backdrop-blur">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="font-heading text-xl font-semibold tracking-tight">HelpDesk</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">ITSM Suite</div>
            </div>
          </div>

          <div className="max-w-md space-y-6">
            <h1 className="font-heading text-4xl font-semibold tracking-tight lg:text-5xl">
              Centralize suporte, inventário e conhecimento.
            </h1>
            <p className="text-base text-white/80">
              Plataforma completa para abertura de chamados, controle de estoque e
              atendimento via WhatsApp — pensada para times modernos.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4 text-sm">
              <div><div className="font-mono text-2xl">3</div><div className="text-xs text-white/60">Perfis de acesso</div></div>
              <div><div className="font-mono text-2xl">∞</div><div className="text-xs text-white/60">Chamados</div></div>
              <div><div className="font-mono text-2xl">24/7</div><div className="text-xs text-white/60">Disponível</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center bg-background px-6 py-12 md:px-16">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">Bem-vindo</div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight">Acesse sua conta</h2>
            <p className="mt-2 text-sm text-muted-foreground">Entre com suas credenciais ou crie uma conta como cliente.</p>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="tab-login">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Criar conta</TabsTrigger>
            </TabsList>
            <TabsContent value="login" className="mt-6">
              <form onSubmit={submitLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email" type="email" required
                    value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="voce@empresa.com"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password" type="password" required
                    value={loginPass} onChange={(e) => setLoginPass(e.target.value)}
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
                </Button>
              </form>

              <div className="mt-6 rounded-md border border-border p-4">
                <div className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">Contas Demo</div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => fillDemo("025guilherme02@gmail.com", "admin123")} data-testid="demo-admin-btn">
                    Admin
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fillDemo("tecnico@demo.com", "tecnico123")} data-testid="demo-tecnico-btn">
                    Técnico
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fillDemo("cliente@demo.com", "cliente123")} data-testid="demo-cliente-btn">
                    Cliente
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <div className="rounded-md border border-border p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Novo cliente? Use nossa página de cadastro completa para criar sua conta.
                </p>
                <Link to="/cadastro">
                  <Button className="mt-4 w-full" data-testid="goto-register-btn">Ir para o cadastro</Button>
                </Link>
                <p className="mt-3 text-xs text-muted-foreground">
                  Contas criadas por lá iniciam com perfil <b>Cliente</b>.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
